import { zodResolver } from '@hookform/resolvers/zod';
import { ForgotPasswordInput } from '@uit/shared/schemas/auth';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import s from './LoginPage.module.scss';

export function ForgotPasswordPage() {
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordInput>({ resolver: zodResolver(ForgotPasswordInput) });

  const onSubmit = async (data: ForgotPasswordInput): Promise<void> => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        setError('Could not send the reset email. Please try again.');
        return;
      }
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={s.page}>
      <div className={s.card}>
        <h1 className={s.title}>Forgot password</h1>
        <p className={s.subtitle}>ultimumIT portal — reset your password</p>

        {done ? (
          <>
            <p role="status" style={{ fontSize: 14, color: '#18181b' }}>
              If an account exists for that email, a reset link is on its way. The link expires in
              1&nbsp;hour.
            </p>
            <p style={{ marginTop: 16, fontSize: 13 }}>
              <Link to="/">← Back to sign in</Link>
            </p>
          </>
        ) : (
          <form
            onSubmit={(e) => {
              void handleSubmit(onSubmit)(e);
            }}
            noValidate
          >
            {error && (
              <div className={s.formError} role="alert">
                {error}
              </div>
            )}
            <div className={s.field}>
              <label className={s.label} htmlFor="email">
                Email
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                className={s.input}
                aria-invalid={!!errors.email}
                {...register('email')}
              />
              {errors.email && <div className={s.fieldError}>{errors.email.message}</div>}
            </div>
            <button type="submit" className={s.button} disabled={submitting}>
              {submitting ? 'Sending…' : 'Send reset link'}
            </button>
            <p style={{ marginTop: 12, fontSize: 13, textAlign: 'center' }}>
              <Link to="/">← Back to sign in</Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
